import { motion } from "framer-motion";
import { ArrowRight, Mail, ShieldCheck, Clock4, Wrench } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const HERO_IMG =
  "https://images.unsplash.com/photo-1615406020658-6c4b805f1f30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgxMTkxODI5fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#64748B] mb-6"
          >
            <span className="w-8 h-px bg-[#0A192F]" />
            {BUSINESS.area}
          </span>
          <h1
            data-testid="hero-headline"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-[#0A192F] tracking-tight"
          >
            Professional Building & Maintenance Services{" "}
            <span className="relative inline-block">
              You Can Trust
              <span className="absolute left-0 -bottom-2 h-1 w-full bg-[#0A192F] rounded-full" />
            </span>
          </h1>
          <p
            data-testid="hero-subheading"
            className="mt-8 text-base sm:text-lg leading-relaxed text-[#334155] max-w-2xl"
          >
            {BUSINESS.tagline}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-semibold px-6 py-4 rounded-md hover:bg-[#1E3A8A] transition-colors group"
            >
              Contact Us Now
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-cta-email"
              href={BUSINESS.emailHref}
              className="inline-flex items-center justify-center gap-2 border-2 border-[#0A192F] text-[#0A192F] font-semibold px-6 py-4 rounded-md hover:bg-[#F8FAFC] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { icon: ShieldCheck, label: "Fully Qualified" },
              { icon: Clock4, label: "Fast Response" },
              { icon: Wrench, label: "All Trades" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-[#0A192F]" strokeWidth={1.6} />
                <span className="text-sm font-medium text-[#0A192F]">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-4 border border-[#0A192F]/15 rounded-md" />
            <img
              src={HERO_IMG}
              alt="Modern commercial building serviced by Perficient Building Services"
              loading="eager"
              className="relative rounded-md w-full h-[420px] sm:h-[480px] lg:h-[560px] object-cover shadow-xl"
            />
            <div className="absolute -bottom-6 -left-6 hidden md:flex flex-col bg-[#0A192F] text-white p-6 rounded-md shadow-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                Domestic & Commercial
              </span>
              <span className="mt-2 font-heading text-2xl font-semibold">
                Trusted Tradespeople
              </span>
              <span className="text-sm text-white/80 mt-1">
                One call. Every trade. Done right.
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
